
import Vue from "vue";
import { mapGetters } from "vuex";

export interface Vendor {
  type: string; // Corresponds to Vendor enum in backend
  displayName: string;
  authorizationEndpoint: string; // Should point to /api/v1/oauth2/authorize/{registrationId}
  redirectUri: string;
}

export default Vue.extend({
  name: "AuthorizationModal",
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    vendor: {
      type: Object as () => Vendor | null,
      default: null,
    },
  },
  computed: {
    ...mapGetters("auth", ["myCenters"]),
    filteredCenters() {
      return this.myCenters.filter(
        (center: { id: number; name: string }) => center.id != null
      );
    },
    isAuthorizeDisabled(): boolean {
      if (!this.vendor) return true;
      if (this.selectedScope === "specific" && this.selectedCenterIds.length === 0) {
        return true; // Disable if 'specific' is selected but no centers are chosen
      }
      return false;
    },
  },
  data() {
    return {
      selectedScope: "all" as "all" | "specific",
      selectedCenterIds: [] as number[],
    };
  },
  methods: {
    /**
     * Initiates the OAuth2 authorization process by redirecting to the backend's authorization initiation endpoint.
     */
    authorize(): void {
      if (!this.vendor) return;

      // Determine centerIds based on user selection; disabled so default to 'all'
      const centerIdsParam =
        this.selectedScope === "specific"
          ? this.selectedCenterIds.join(",")
          : "";

      // Retrieve the backend's base URL from Axios
      const backendBaseURL = this.$axios.defaults.baseURL;

      if (!backendBaseURL) {
        console.error("Axios baseURL is not configured.");
        this.$store.dispatch("messages/addErrorMessage", "Authorization failed: Server configuration error.");
        return;
      }

      // Construct the authorization initiation URL
      // Example: https://your-backend-domain.com/api/v1/oauth2/authorize/gmail?centerIds=1,2,3
      let authorizationInitiationUrl = `${backendBaseURL}${this.vendor.authorizationEndpoint}`;

      // Append centerIds as query parameters if selected
      if (centerIdsParam) {
        try {
          // Use the URL constructor to handle query parameters safely
          const url = new URL(authorizationInitiationUrl);
          url.searchParams.append("centerIds", centerIdsParam);
          authorizationInitiationUrl = url.toString();
        } catch (error) {
          console.error("Invalid authorization initiation URL:", error);
          this.$store.dispatch("messages/addErrorMessage", "Failed to initiate authorization.");
          return;
        }
      }

      // Redirect the user to the authorization initiation endpoint
      window.location.href = authorizationInitiationUrl;
    },
  },
});
